import type { Accessor } from '../function';
import { isFunction } from '../function';
import { isNotNullOrUndefined, isNullOrUndefined } from '../object';

export function groupBy<Item, MapKey>(items: Item[], accessor: Accessor<Item, MapKey>): Map<MapKey, Item[]>;
export function groupBy<Item, ItemProperty extends keyof Item>(items: Item[], key: ItemProperty): Map<Item[ItemProperty], Item[]>;
export function groupBy<Item, MapKey, ItemProperty extends keyof Item = keyof Item>(
  items: Item[],
  keyOrAccessor: ItemProperty | Accessor<Item, MapKey>,
) {
  return items.reduce((map, item) => {
    const mapKey = isFunction(keyOrAccessor) ? keyOrAccessor(item) : item[keyOrAccessor];
    const existingItems = getMapValueOrDefault(map, mapKey, []);

    return map.set(mapKey, [...existingItems, item]);
  },
  new Map<Item[ItemProperty] | MapKey, Item[]>());
}

export function mapBy<Item, MapKey>(items: Item[], accessor: Accessor<Item, MapKey>): Map<MapKey, Item>;
export function mapBy<Item, ItemProperty extends keyof Item>(items: Item[], key: ItemProperty): Map<Item[ItemProperty], Item>;
export function mapBy<Item, Key extends keyof Item>(
  items: Item[],
  keyOrAccessor: Key | Accessor<Item>,
) {
  return new Map(items.map((item) => [
    isFunction(keyOrAccessor) ? keyOrAccessor(item) : item[keyOrAccessor],
    item,
  ]));
}

export function arrayToCountMap<Item>(array: Item[]): Map<Item, number> {
  return array.reduce((countMap, currentValue) => countMap.has(currentValue) 
    ? countMap.set(currentValue, countMap.get(currentValue)! + 1) 
    : countMap.set(currentValue, 1),
  new Map<Item, number>());
}

export function getMapValue<Key, Value>(map: Map<Key, Value>, key: Key | undefined) {
  return isNullOrUndefined(key) ? undefined : map.get(key);
}

export function getRequiredMapValue<Key, Value>(map: Map<Key, Value>, key: Key) {
  const value = map.get(key);

  if (isNullOrUndefined(value)) {
    throw new Error(`No value found for key ${key}`);
  }

  return value;
}

export function getMapValueOrDefault<Key, Value>(map: Map<Key, Value>, key: Key, defaultValue: Value) {
  const value = map.get(key);

  return isNotNullOrUndefined(value) ? value : defaultValue;
}

export function getMapValues<Key, Value>(map: Map<Key, Value>) {
  return [...map.values()];
}

export function getMapKeys<Key, Value>(map: Map<Key, Value>) {
  return [...map.keys()];
}

export function getFlatMapValues<Key, Value>(map: Map<Key, Value[]>) {
  return getMapValues(map).flat();
}

export function getMapEntries<Key, Value>(map: Map<Key, Value>) {
  return [...map.entries()];
}

export function isEmptyMap<Key, Value>(map: Map<Key, Value>) {
  return map.size === 0;
}

export function mapToRecord<Key extends string | number | symbol, Value>(map: Map<Key, Value>) {
  return Object.fromEntries(map.entries());
}

export function iterateOverEntries<Key, Value, ActionResult>(map: Map<Key, Value>, actionOnEntries: (entry: [Key, Value]) => ActionResult) {
  return getMapEntries(map).map(actionOnEntries);
}